import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthHeader } from "react-auth-kit";
import { useApi } from "../home/apiService";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

export type ArticleStatus = "draft" | "published" | "removed";

export interface ArticleSummary {
  id: string;
  slug: string;
  language: string;
  title: string;
  dek: string | null;
  status: ArticleStatus;
  hasCover: boolean;
  coverAlt: string | null;
  wordCount: number | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  removedAt: string | null;
}

export interface ArticleSource {
  label: string;
  detail?: string;
  url?: string;
}

export interface Article extends ArticleSummary {
  bodyMd: string;
  sources: ArticleSource[];
  generation?: Record<string, unknown>;
  coverPrompt?: string | null;
}

// Stable across renders (useApi() returns a new object each render, which would
// re-run every effect that depends on it).
export const useArticlesApi = () => {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  return useMemo(
    () => ({
      list: (all = false): Promise<{ articles: ArticleSummary[] }> =>
        apiRef.current.fetchWithAuth(`/articles${all ? "?all=1" : ""}`),
      get: (key: string): Promise<Article & { error?: string }> =>
        apiRef.current.fetchWithAuth(`/articles/${encodeURIComponent(key)}`),
      setStatus: (id: string, status: ArticleStatus): Promise<ArticleSummary> =>
        apiRef.current._request("PATCH", `/articles/${id}`, { status }),
      remove: (id: string): Promise<{ ok: boolean }> =>
        apiRef.current.fetchWithAuthDelete(`/articles/${id}`),
    }),
    [],
  );
};

// The cover is served behind auth, so <img src> can't load it directly: fetch it
// with the auth header and hand the <img> an object URL.
export const useCoverUrl = (id: string | null | undefined, hasCover: boolean) => {
  const authHeader = useAuthHeader();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!id || !hasCover) {
      setUrl(null);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    fetch(`${apiUrl}/api/articles/${id}/cover`, { headers: { Authorization: authHeader() } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!blob || cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hasCover]);
  return url;
};
